/**
 * Elevation utility functions for race tracking
 */

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param {number} lat1 - Latitude of point 1
 * @param {number} lon1 - Longitude of point 1
 * @param {number} lat2 - Latitude of point 2
 * @param {number} lon2 - Longitude of point 2
 * @returns {number} Distance in meters
 */
export function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Calculate elevation statistics from GPS points
 * @param {Array} gpsPoints - Array of GPS points with elevation
 * @returns {Object} Elevation statistics
 */
export function calculateElevationStats(gpsPoints) {
    if (!gpsPoints || gpsPoints.length === 0) {
        return {
            totalGain: 0,
            totalLoss: 0,
            minElevation: 0,
            maxElevation: 0,
            currentElevation: 0,
        };
    }

    let totalGain = 0;
    let totalLoss = 0;
    let minElevation = Infinity;
    let maxElevation = -Infinity;

    // Filter points with valid elevation
    const validPoints = gpsPoints.filter(p => p.elevation != null && p.elevation !== 0);

    if (validPoints.length === 0) {
        return {
            totalGain: 0,
            totalLoss: 0,
            minElevation: 0,
            maxElevation: 0,
            currentElevation: 0,
        };
    }

    for (let i = 0; i < validPoints.length; i++) {
        const curr = validPoints[i].elevation;

        minElevation = Math.min(minElevation, curr);
        maxElevation = Math.max(maxElevation, curr);

        if (i > 0) {
            const prev = validPoints[i - 1].elevation;
            const diff = curr - prev;

            // Use a threshold to filter out GPS noise (2m)
            if (diff > 2) {
                totalGain += diff;
            } else if (diff < -2) {
                totalLoss += Math.abs(diff);
            }
        }
    }

    return {
        totalGain: Math.round(totalGain),
        totalLoss: Math.round(totalLoss),
        minElevation: Math.round(minElevation),
        maxElevation: Math.round(maxElevation),
        currentElevation: Math.round(validPoints[validPoints.length - 1]?.elevation || 0),
    };
}

/**
 * Calculate cumulative distance with elevation for charting
 * @param {Array} gpsPoints - Array of GPS points with lat, lng, elevation
 * @returns {Array} Array of { distance, elevation } for charting
 */
export function calculateElevationProfile(gpsPoints) {
    if (!gpsPoints || gpsPoints.length === 0) {
        return [];
    }

    const profile = [];
    let cumulativeDistance = 0;

    for (let i = 0; i < gpsPoints.length; i++) {
        const point = gpsPoints[i];
        const lat = point.lat || point[0];
        const lng = point.lng || point.lon || point[1];
        const elevation = point.elevation || point[2] || 0;

        if (i > 0) {
            const prev = gpsPoints[i - 1];
            const prevLat = prev.lat || prev[0];
            const prevLng = prev.lng || prev.lon || prev[1];
            cumulativeDistance += haversineDistance(prevLat, prevLng, lat, lng);
        }

        profile.push({
            distance: cumulativeDistance,
            elevation: elevation,
            lat,
            lng,
            timestamp: point.timestamp || point.epoch,
        });
    }

    return profile;
}

/**
 * Downsample elevation profile for rendering (reduce number of points)
 * @param {Array} profile - Full elevation profile
 * @param {number} maxPoints - Maximum number of points to return
 * @returns {Array} Downsampled profile
 */
export function downsampleProfile(profile, maxPoints = 200) {
    if (!profile || profile.length <= maxPoints) {
        return profile;
    }

    const step = Math.ceil(profile.length / maxPoints);
    const downsampled = [];

    for (let i = 0; i < profile.length; i += step) {
        downsampled.push(profile[i]);
    }

    // Always include the last point
    if (downsampled[downsampled.length - 1] !== profile[profile.length - 1]) {
        downsampled.push(profile[profile.length - 1]);
    }

    return downsampled;
}

/**
 * Convert meters to feet
 * @param {number} meters - Value in meters
 * @returns {number} Value in feet
 */
export function metersToFeet(meters) {
    return Math.round(meters * 3.28084);
}

/**
 * Format elevation for display
 * @param {number} elevation - Elevation in meters
 * @param {boolean} imperial - Use imperial units (feet)
 * @returns {string} Formatted elevation string
 */
export function formatElevation(elevation, imperial = false) {
    if (imperial) {
        return `${metersToFeet(elevation)} ft`;
    }
    return `${Math.round(elevation)} m`;
}

/**
 * Format distance for display
 * @param {number} meters - Distance in meters
 * @param {boolean} imperial - Use imperial units (miles)
 * @returns {string} Formatted distance string
 */
export function formatDistance(meters, imperial = false) {
    if (imperial) {
        const miles = meters / 1609.344;
        return miles >= 1 ? `${miles.toFixed(1)} mi` : `${Math.round(meters * 3.28084)} ft`;
    }
    const km = meters / 1000;
    return km >= 1 ? `${km.toFixed(1)} km` : `${Math.round(meters)} m`;
}
